import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, adminHeaders } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Plus } from "lucide-react";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export function InvitesPanel() {
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ email: "", full_name: "", organization: "", role: "pilot_user" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/invites`, { headers: adminHeaders() });
      setInvites(r.data || []);
    } catch { toast.error("Unable to load invites."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/invites`, form, { headers: adminHeaders() });
      toast.success("Invitation created.");
      setForm({ email: "", full_name: "", organization: "", role: "pilot_user" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Unable to create invite."); }
  };

  const revoke = async (token) => {
    try {
      await axios.delete(`${API}/admin/invites/${token}`, { headers: adminHeaders() });
      toast.success("Invitation revoked.");
      load();
    } catch { toast.error("Unable to revoke."); }
  };

  const copyLink = (token) => {
    navigator.clipboard?.writeText(`${ORIGIN}/invite/${token}`);
    toast.success("Invitation link copied.");
  };

  return (
    <div data-testid="admin-invites">
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end p-4 border border-iv-line bg-iv-surface mb-6">
        <label className="iv-field"><span>Full Name</span><input required value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} /></label>
        <label className="iv-field"><span>Email</span><input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></label>
        <label className="iv-field"><span>Organization</span><input required value={form.organization} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))} /></label>
        <label className="iv-field"><span>Role</span>
          <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
            <option value="pilot_user">Pilot User</option>
            <option value="pilot_admin">Organization Admin</option>
            <option value="investment_professional">Investment Professional</option>
            <option value="committee_viewer">Committee Viewer</option>
          </select>
        </label>
        <button type="submit" className="iv-btn iv-btn--primary"><Plus size={15} /> Create Invite</button>
      </form>

      <div className="border-t border-iv-line">
        {loading ? <p className="py-10 text-center font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">Loading…</p>
          : invites.length === 0 ? <p className="py-10 text-center font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">No invitations yet.</p>
          : invites.map((inv) => (
            <div key={inv.token} className="grid grid-cols-12 gap-3 items-center px-3 py-4 border-b border-iv-line hover:bg-white/40">
              <div className="col-span-12 md:col-span-5">
                <div className="text-[14px] text-iv-fg">{inv.full_name} · <span className="text-iv-fg-2">{inv.organization}</span></div>
                <div className="font-mono text-[10.5px] text-iv-fg-3">{inv.email} · {inv.role}</div>
              </div>
              <div className="col-span-6 md:col-span-3"><span className={`iv-chip ${inv.status === "Pending" ? "iv-chip--gold" : inv.status === "Accepted" ? "iv-chip--green" : ""}`}>{inv.status}</span></div>
              <div className="col-span-6 md:col-span-4 md:justify-self-end flex gap-2">
                {inv.status === "Pending" && (
                  <>
                    <button onClick={() => copyLink(inv.token)} className="iv-btn iv-btn--ghost"><Copy size={13} /> Link</button>
                    <button onClick={() => revoke(inv.token)} className="iv-btn iv-btn--ghost text-iv-fg-3">Revoke</button>
                  </>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

export function TeamLinksPanel() {
  const [links, setLinks] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ organization: "", organization_id: "", default_role: "committee_viewer", max_uses: 10, ttl_days: 7, allowed_email_domain: "", require_domain_match: true });

  const load = useCallback(async () => {
    try {
      const [l, u] = await Promise.all([
        axios.get(`${API}/admin/team-links`, { headers: adminHeaders() }),
        axios.get(`${API}/admin/users`, { headers: adminHeaders() }),
      ]);
      setLinks(l.data || []);
      setUsers(u.data || []);
    } catch { toast.error("Unable to load team links."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Organizations we can issue links for (must have at least one user/invite)
  const orgs = Array.from(new Map(users.map((u) => [u.organization_id, u.organization])).entries())
    .map(([id, name]) => ({ id, name }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.organization_id) return toast.error("Select an approved organization first.");
    try {
      const payload = { ...form, max_uses: Number(form.max_uses), ttl_days: Number(form.ttl_days), allowed_email_domain: form.allowed_email_domain || null };
      await axios.post(`${API}/admin/team-links`, payload, { headers: adminHeaders() });
      toast.success("Team link created.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Unable to create link."); }
  };

  const revoke = async (token) => {
    try {
      await axios.delete(`${API}/admin/team-links/${token}`, { headers: adminHeaders() });
      toast.success("Team link revoked.");
      load();
    } catch { toast.error("Unable to revoke."); }
  };

  const copyLink = (token) => {
    navigator.clipboard?.writeText(`${ORIGIN}/join/${token}`);
    toast.success("Team link copied.");
  };

  return (
    <div data-testid="admin-team-links">
      <p className="text-[13.5px] text-iv-fg-2 mb-4 max-w-2xl">
        Issue an organization-scoped <strong className="text-iv-fg">Invite Your Team</strong> link for an approved pilot organization.
        Shared links only grant Committee Viewer or Investment Professional roles — never administrator access.
      </p>
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end p-4 border border-iv-line bg-iv-surface mb-6">
        <label className="iv-field md:col-span-2"><span>Organization</span>
          <select value={form.organization_id} onChange={(e) => {
            const o = orgs.find((x) => x.id === e.target.value);
            setForm((f) => ({ ...f, organization_id: e.target.value, organization: o?.name || "" }));
          }}>
            <option value="">Select approved org…</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        <label className="iv-field"><span>Role</span>
          <select value={form.default_role} onChange={(e) => setForm((f) => ({ ...f, default_role: e.target.value }))}>
            <option value="committee_viewer">Committee Viewer</option>
            <option value="investment_professional">Investment Professional</option>
          </select>
        </label>
        <label className="iv-field"><span>Max Uses</span><input type="number" min={1} max={100} value={form.max_uses} onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))} /></label>
        <label className="iv-field"><span>Valid (days)</span><input type="number" min={1} max={30} value={form.ttl_days} onChange={(e) => setForm((f) => ({ ...f, ttl_days: e.target.value }))} /></label>
        <label className="iv-field"><span>Email Domain</span><input value={form.allowed_email_domain} onChange={(e) => setForm((f) => ({ ...f, allowed_email_domain: e.target.value }))} placeholder="optional" /></label>
        <button type="submit" className="iv-btn iv-btn--primary md:col-span-6"><Plus size={15} /> Create Team Link</button>
      </form>

      <div className="border-t border-iv-line">
        {links.length === 0 ? <p className="py-10 text-center font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">No team links yet.</p>
          : links.map((l) => (
            <div key={l.link_token} className="grid grid-cols-12 gap-3 items-center px-3 py-4 border-b border-iv-line hover:bg-white/40">
              <div className="col-span-12 md:col-span-5">
                <div className="text-[14px] text-iv-fg">{l.organization}</div>
                <div className="font-mono text-[10.5px] text-iv-fg-3">{l.default_role} · {l.uses}/{l.max_uses} used {l.allowed_email_domain ? `· @${l.allowed_email_domain}` : ""}</div>
              </div>
              <div className="col-span-6 md:col-span-3"><span className={`iv-chip ${l.status === "Active" ? "iv-chip--green" : ""}`}>{l.status}</span></div>
              <div className="col-span-6 md:col-span-4 md:justify-self-end flex gap-2">
                {l.status === "Active" && (
                  <>
                    <button onClick={() => copyLink(l.link_token)} className="iv-btn iv-btn--ghost"><Copy size={13} /> Link</button>
                    <button onClick={() => revoke(l.link_token)} className="iv-btn iv-btn--ghost text-iv-fg-3">Revoke</button>
                  </>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

export function AuditPanel() {
  const [log, setLog] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/audit-log`, { headers: adminHeaders() });
        setLog(r.data || []);
      } catch { toast.error("Unable to load audit log."); }
    })();
  }, []);
  return (
    <div data-testid="admin-audit" className="border-t border-iv-line">
      {log.length === 0 ? <p className="py-10 text-center font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">No audit events yet.</p>
        : log.map((e) => (
          <div key={e.id} className="grid grid-cols-12 gap-3 px-3 py-3 border-b border-iv-line font-mono text-[11.5px]">
            <div className="col-span-3 text-iv-fg-3">{new Date(e.at).toLocaleString()}</div>
            <div className="col-span-3 text-iv-green uppercase tracking-wider">{e.action}</div>
            <div className="col-span-6 text-iv-fg-2 truncate">{JSON.stringify(e.details)}</div>
          </div>
        ))}
    </div>
  );
}
